import { isSafeUrl } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	type SMART_LINK_TYPE,
} from '@atlaskit/editor-common/analytics';
import type { EmbedCardTransformAttrs } from '@atlaskit/editor-common/card';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import type { Command } from '@atlaskit/editor-common/types';
import { processRawValue } from '@atlaskit/editor-common/utils';
import type { Node, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { NATIVE_EMBED_PARAMETER_DEFAULTS, setParameters } from '@atlaskit/native-embeds-common';
import type {
	AlignmentValue,
	CardToNativeEmbedTransformOptions,
	NativeEmbedLayout,
} from '@atlaskit/native-embeds-common';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';

const LAYOUT_TO_ALIGNMENT: Record<NativeEmbedLayout, AlignmentValue> = {
	center: 'center',
	'full-width': 'center',
	wide: 'center',
	'wrap-left': 'wrap-left',
	'wrap-right': 'wrap-right',
	'align-start': 'left',
	'align-end': 'right',
};

function mapLayoutToAlignment(layout: string = 'center'): AlignmentValue {
	return LAYOUT_TO_ALIGNMENT[layout as NativeEmbedLayout] ?? 'center';
}

/**
 * Transforms native embed ADF attrs into a native embed extension Node when the URL
 * is supported by a registered experience.
 *
 * Returns undefined if:
 * - cc-maui-experiment is disabled
 * - URL is not safe
 * - URL is not supported by any registered experience
 */
export function cardToNativeEmbedNode(
	schema: Schema,
	attrs: EmbedCardTransformAttrs,
): Node | undefined {
	if (!expVal('cc-maui-experiment', 'isEnabled', false)) {
		return undefined;
	}

	const { url, layout = 'center', width, originalHeight, originalWidth } = attrs;

	if (!isSafeUrl(url)) {
		return undefined;
	}

	const manifest = getNativeEmbedFacade().getManifestByUrl(url);
	if (!manifest) {
		return undefined;
	}

	const macroParams = setParameters(
		{},
		{
			...NATIVE_EMBED_PARAMETER_DEFAULTS,
			url,
			alignment: mapLayoutToAlignment(layout),
		},
	);

	const extensionAdf: ADFEntity = {
		type: 'extension',
		attrs: {
			extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${manifest.id}`,
			extensionType: NATIVE_EMBED_EXTENSION_TYPE,
			parameters: {
				...macroParams,
				macroMetadata: {
					// Preserved data in case of revert.
					url,
					layout,
					width: width ?? undefined,
					originalHeight: originalHeight ?? undefined,
					originalWidth: originalWidth ?? undefined,
				},
			},
		},
	};

	return processRawValue(schema, extensionAdf);
}

export function insertedContentToNativeEmbedNode(schema: Schema, node: Node): Node | undefined {
	if (node.type === schema.nodes.embedCard) {
		return cardToNativeEmbedNode(schema, {
			url: node.attrs.url,
			layout: node.attrs.layout,
			width: node.attrs.width,
			originalHeight: node.attrs.originalHeight,
			originalWidth: node.attrs.originalWidth,
		});
	}

	if (
		node.type === schema.nodes.paragraph &&
		node.childCount === 1 &&
		node.firstChild?.isText &&
		node.firstChild.marks.length
	) {
		const linkMarkType = schema.marks.link;
		const linkMark = linkMarkType
			? node.firstChild.marks.find((mark) => mark.type === linkMarkType)
			: undefined;
		const url = linkMark?.attrs.href;
		if (url) {
			return cardToNativeEmbedNode(schema, {
				url,
				layout: 'center',
				width: undefined,
			});
		}
	}

	return undefined;
}

function appearanceForNodeType(schema: Schema, nodeType: NodeType) {
	if (nodeType === schema.nodes.inlineCard) {
		return 'inline';
	}
	if (nodeType === schema.nodes.blockCard) {
		return 'block';
	}
	if (nodeType === schema.nodes.embedCard) {
		return 'embed';
	}
	return 'url';
}

/**
 * Creates a Command that replaces a selected inline/block card with a native embed
 * when the URL is supported.
 *
 * Handles document structure: replaceWith, trailing space insertion, selection,
 * and preceding paragraph deletion.
 *
 * Use augmentTransaction to add card-plugin concerns (datasource stash, analytics,
 * link metadata).
 *
 * Returns false if no node selected, URL not supported, or not an inline/block card.
 */
export function createCardToNativeEmbedTransform(
	options?: CardToNativeEmbedTransformOptions,
): Command {
	if (!expVal('cc-maui-experiment', 'isEnabled', false)) {
		return () => false;
	}

	const { augmentTransaction, editorAnalyticsApi } = options ?? {};

	return (state, dispatch) => {
		const selectedNode =
			state.selection instanceof NodeSelection ? state.selection.node : undefined;
		if (!selectedNode) {
			return false;
		}

		const url = selectedNode.attrs.url ?? selectedNode.attrs.data?.url;
		if (!url) {
			return false;
		}

		const previousType = appearanceForNodeType(state.schema, selectedNode.type) as SMART_LINK_TYPE;

		const embedAttrs: EmbedCardTransformAttrs = {
			url,
			layout: selectedNode.attrs.layout ?? 'center',
			width: selectedNode.attrs.width ?? NATIVE_EMBED_PARAMETER_DEFAULTS.width,
			originalHeight: selectedNode.attrs.originalHeight ?? NATIVE_EMBED_PARAMETER_DEFAULTS.height,
			originalWidth: selectedNode.attrs.originalWidth ?? NATIVE_EMBED_PARAMETER_DEFAULTS.width,
		};

		const extensionNode = cardToNativeEmbedNode(state.schema, embedAttrs);
		if (!extensionNode) {
			return false;
		}

		const { from, to } = state.selection;
		const tr = state.tr.replaceWith(from, to, extensionNode);

		if (tr.doc.nodeSize - 2 === to) {
			tr.insertText(' ', to);
		}

		tr.setSelection(TextSelection.create(tr.doc, to + 1));

		const previousNodePos = from - 1 > 0 ? from - 1 : 0;
		const previousNode = tr.doc.nodeAt(previousNodePos);
		if (previousNode?.type?.name === 'paragraph') {
			tr.delete(previousNodePos, from);
		}

		augmentTransaction?.(tr, state);

		if (dispatch) {
			dispatch(tr.scrollIntoView());
			editorAnalyticsApi?.attachAnalyticsEvent({
				action: ACTION.CHANGED_TYPE,
				actionSubject: ACTION_SUBJECT.NATIVE_EMBED,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					newType: 'nativeEmbed',
					previousType,
				},
			})(tr);
		}
		return true;
	};
}
